import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Mail, BadgeCheck, ShieldCheck, RefreshCw } from "lucide-react";
import { MaskedLines, Reveal, ChapterHeading } from "@/components/motion";
import JsonLd from "@/components/JsonLd";
import WireframeRoom from "@/components/WireframeRoom";
import { EMAIL, PORTRAIT_URL } from "@/constants/site";
import { MODALITIES } from "@/constants/modalities";
import { ARTICLES } from "@/constants/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const MARQUEE_ITEMS = [
  "DIAGNOSTIC X-RAY",
  "CT",
  "CBCT",
  "FLUOROSCOPY & C-ARMS",
  "MAMMOGRAPHY",
  "DENTAL",
  "VETERINARY",
  "STATE-SPECIFIC CALCULATIONS",
  "CONSTRUCTION-READY REPORTS",
  "FIVE-DAY TURNAROUND",
];

const REPORT_ITEMS = [
  { n: "01", title: "Barrier-by-barrier calculations", body: "Walls, floors, ceilings, doors, and windows — every barrier calculated for your exact equipment and layout." },
  { n: "02", title: "Material specifications", body: "Specifications your contractor can price, source, and build — no guesswork at the job site." },
  { n: "03", title: "State-specific documentation", body: "Prepared for plan review and regulatory submission in your project's jurisdiction." },
  { n: "04", title: "Direct coordination", body: "The physicist works with your architects, contractors, and equipment vendors to resolve questions early." },
];

const PROCESS_STEPS = [
  { n: "1.", title: "Tell us about your project", body: "A few project details are all we need — floor plan, equipment, and workload." },
  { n: "2.", title: "The physicist prepares your design", body: "Every calculation and recommendation is prepared, reviewed, and signed off directly by an approved physicist." },
  { n: "3.", title: "Receive your report", body: "Ready for plan review, construction planning, and regulatory submission." },
];

const PROMISES = [
  { icon: BadgeCheck, title: "State-approved design", body: "Prepared to your radiation-control authority's requirements — ready for plan review and approval." },
  { icon: ShieldCheck, title: "90-day money-back guarantee", body: "If you are not satisfied for any reason within 90 days of delivery, you get a full refund. No fine print." },
  { icon: RefreshCw, title: "Unlimited recalculations for 5 years", body: "Machine changes, workload increases, or a room revision — recalculated free for five years after delivery." },
];

const FAQS = [
  { q: "When is a radiation shielding design required?", a: "Most new, relocated, remodeled, or upgraded rooms that use ionizing radiation need a shielding review. Requirements vary by state and project type." },
  { q: "What information do you need to begin?", a: "A floor plan, equipment specifications, expected workload, and details about the spaces above, below, and beside the room." },
  { q: "How long does a shielding design take?", a: "We typically respond within one business day and deliver the completed report within five business days after receiving complete project information." },
  { q: "Which projects do you support?", a: "We support X-ray, CT and CBCT, fluoroscopy and C-arms, mammography, dental, and veterinary imaging projects." },
];

const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden border-b border-border bg-blueprint-grid pt-16">
    <div className="spotlight pointer-events-none absolute inset-0" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-5 pb-16 pt-14 md:px-8 md:pb-24 md:pt-20 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
      <div>
        <Reveal y={12}>
          <p className="eyebrow" data-testid="hero-eyebrow">Heartland Physics — Radiation Shielding Design</p>
        </Reveal>
        <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.04] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          <MaskedLines
            delay={0.15}
            lines={[
              "Work directly with",
              "the physicist",
              "behind your",
              "shielding design.",
            ]}
          />
        </h1>
        <Reveal delay={0.75} className="mt-7 max-w-xl">
          <p className="text-base leading-relaxed text-ink/70 md:text-lg" data-testid="hero-subheading">
            Heartland Physics is a principal-led practice. Work directly with Lancer Smith from floor-plan review through a clear, construction-ready shielding report.
          </p>
        </Reveal>
        <Reveal delay={0.9} className="mt-9 flex flex-wrap items-center gap-4">
          <Link
            to="/request"
            data-testid="hero-cta-request"
            className="group flex items-center gap-2 bg-blueprint px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
          >
            Request a Shielding Design
            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="hero-cta-email"
            className="group flex items-center gap-2 border border-ink/25 bg-white/60 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
          >
            <Mail size={16} />
            Email Lancer@HeartlandPhysics.com
          </a>
        </Reveal>
        <Reveal delay={1.05} className="mt-10">
          <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/50" data-testid="hero-trust-strip">
            <span>State-Specific Calculations</span>
            <span className="text-blueprint">/</span>
            <span>Direct Access to Physicist</span>
            <span className="text-blueprint">/</span>
            <span>Clear, Buildable Reports</span>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.5} y={16} className="hidden lg:block">
        <div className="relative aspect-[560/440] border border-border bg-white/70">
          <WireframeRoom />
        </div>
      </Reveal>
    </div>
  </section>
);

const EditorialMarquee = () => (
  <div data-testid="editorial-marquee" className="border-b border-border bg-ink py-4">
    <Marquee speed={28} gradient={false} pauseOnHover className="marquee-reduced-motion">
      {MARQUEE_ITEMS.map((item) => (
        <span key={item} className="mx-8 flex items-center gap-8 font-mono text-xs uppercase tracking-[0.3em] text-white/70">
          {item}
          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-azure" aria-hidden="true" />
        </span>
      ))}
    </Marquee>
  </div>
);

const ValueProp = () => (
  <section data-testid="value-prop-section" className="border-b border-border">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-[1fr_1fr] md:px-8 md:py-24 lg:gap-20">
      <Reveal>
        <p className="eyebrow">The promise</p>
        <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink md:text-xl">
          Clear answers. Buildable reports. Faster starts.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-base leading-relaxed text-ink/70">
          Typical shielding report turnaround is five business days after we receive complete project information — and we respond within one business day. No handoffs, no sales team: the person reviewing your project is the person responsible for the calculations.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2">
          <div className="bg-white p-6" data-testid="stat-quote">
            <p className="font-heading text-4xl font-extrabold tracking-tight text-blueprint">1<span className="text-lg align-top"> day</span></p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Response time</p>
          </div>
          <div className="bg-white p-6" data-testid="stat-report">
            <p className="font-heading text-4xl font-extrabold tracking-tight text-blueprint">5<span className="text-lg align-top"> days</span></p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Typical report delivery</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ReportIncludes = () => (
  <section data-testid="report-includes-section" className="border-b border-border bg-white">
    <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28">
      <Reveal>
        <ChapterHeading number="01" eyebrow="Deliverable" title="What your shielding report includes" />
      </Reveal>
      <div className="mt-12 grid gap-px border border-border bg-border md:grid-cols-2 lg:grid-cols-4">
        {REPORT_ITEMS.map((item, i) => (
          <Reveal key={item.n} delay={i * 0.08} className="group bg-white p-7 transition-colors duration-300 hover:bg-paper">
            <p className="font-mono text-xs text-blueprint">{item.n}</p>
            <h3 className="mt-4 font-heading text-base font-bold tracking-tight text-ink">{item.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">{item.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Services = () => (
  <section data-testid="services-section" className="border-b border-border bg-blueprint-grid">
    <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28">
      <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="eyebrow">Services</p>
          <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink md:text-xl">
            One shielding partner for every imaging project
          </h2>
        </div>
        <Link to="/request" data-testid="services-cta-request" className="group flex w-fit items-center gap-2 text-sm font-semibold text-blueprint transition-colors hover:text-ink">
          Start a project <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
        {MODALITIES.map((m, i) => {
          const Icon = m.icon;
          return (
            <Reveal key={m.slug} delay={i * 0.06} className="h-full">
              <Link
                to={`/shielding-design/${m.slug}`}
                data-testid={`services-card-${m.slug}`}
                className="group relative flex h-full flex-col bg-white p-7 transition-colors duration-300 hover:bg-ink"
              >
                <Icon size={20} className="text-blueprint transition-colors duration-300 group-hover:text-azure" />
                <h3 className="mt-5 font-heading text-base font-bold tracking-tight text-ink transition-colors duration-300 group-hover:text-white">
                  {m.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60 transition-colors duration-300 group-hover:text-white/60">{m.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-blueprint opacity-0 transition-all duration-300 group-hover:opacity-100">
                  Explore this service <ArrowRight size={13} />
                </span>
              </Link>
            </Reveal>
          );
        })}
        <Reveal delay={0.36} className="relative overflow-hidden bg-white p-7">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-ink/50">Prefer email?</p>
          <p className="mt-4 font-heading text-base font-bold tracking-tight text-ink">
            Send over your project details — response within one business day.
          </p>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="services-cta-email"
            className="mt-6 flex w-fit items-center gap-2 border border-ink/25 px-5 py-3 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
          >
            <Mail size={15} /> {EMAIL}
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="border-b border-border bg-white">
    <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28">
      <Reveal>
        <ChapterHeading number="02" eyebrow="Process" title="From floor plan to state-ready report" />
      </Reveal>
      <p className="mt-4 max-w-xl text-sm text-ink/60 md:ml-[104px]">
        A disciplined process designed to keep your project moving.
      </p>
      <div className="mt-12 grid gap-px border border-border bg-border md:grid-cols-3">
        {PROCESS_STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08} className="bg-white p-7">
            <p className="font-heading text-4xl font-extrabold tracking-tight text-blueprint">{s.n}</p>
            <h3 className="mt-5 font-heading text-base font-bold tracking-tight text-ink">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">{s.body}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1} className="mt-8">
        <div className="flex flex-col items-start justify-between gap-5 border border-blueprint/30 bg-blueprint/5 p-7 md:flex-row md:items-center" data-testid="pricing-note">
          <p className="text-sm leading-relaxed text-ink/75">
            <span className="font-semibold text-ink">Transparent pricing.</span> Every room is <span className="font-semibold text-blueprint">$950</span> — prepaid at intake, before work begins. If anything changes down the road, recalculations are free for five years.
          </p>
          <Link
            to="/request"
            data-testid="process-cta-request"
            className="group flex shrink-0 items-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
          >
            Start your project <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const PromiseBand = () => (
  <section data-testid="promise-section" className="border-b border-border bg-blueprint-grid">
    <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28">
      <Reveal>
        <p className="eyebrow">The Heartland promise</p>
        <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink md:text-xl">
          One fixed price. Zero risk. Five years of cover.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-px border border-border bg-border md:grid-cols-3">
        {PROMISES.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08} className="h-full bg-white p-7">
            <p.icon size={20} className="text-blueprint" />
            <h3 className="mt-5 font-heading text-base font-bold tracking-tight text-ink">{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">{p.body}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-8 border border-blueprint/30 bg-white px-6 py-4 text-sm leading-relaxed text-ink/75" data-testid="promise-signoff">
          Every calculation is personally prepared and signed off by <span className="font-semibold text-ink">Medical Health Physicist Lancer Smith, M.S.</span>
        </p>
      </Reveal>
    </div>
  </section>
);

const WhyItMatters = () => (
  <section data-testid="why-shielding-section" className="border-b border-border bg-white">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:px-8 md:py-28 lg:grid-cols-[1fr_0.85fr] lg:items-center">
      <div>
        <Reveal>
          <ChapterHeading number="03" eyebrow="Why it matters" title="Protect people. Avoid overbuilding. Pass review." />
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70">
            A radiation shielding design calculates the barriers needed around X-ray, CT, fluoroscopy, mammography, dental, and veterinary imaging rooms. Using equipment output, workload, room layout, and adjacent occupancy, Lancer prepares a state-specific report for plan review and construction.
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-ink/70">
            The goal is simple: protect people, avoid overbuilding, and help your project move through regulatory review with confidence.
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-9 flex flex-wrap gap-4">
          <Link to="/request" data-testid="why-cta-request" className="group flex items-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink">
            Get your project started <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/about" data-testid="why-cta-about" className="group flex items-center gap-2 border border-ink/25 px-6 py-3.5 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint">
            About the practice <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a href={`mailto:${EMAIL}`} data-testid="why-cta-email" className="group flex items-center gap-2 px-6 py-3.5 text-sm font-semibold text-blueprint transition-colors hover:text-ink">
            <Mail size={15} /> Email Lancer directly
          </a>
        </Reveal>
      </div>
      <Reveal delay={0.15}>
        <div className="relative">
          <div className="spotlight absolute -inset-6" />
          <div className="clip-chamfer relative border border-border bg-white p-2">
            <div className="clip-chamfer aspect-[4/5] w-full bg-[#EDF2F9] bg-blueprint-grid">
              <img
                src={PORTRAIT_URL}
                alt="Lancer Smith, M.S. — principal physicist at Heartland Physics"
                loading="lazy"
                data-testid="about-teaser-portrait"
                className="h-full w-full object-contain object-bottom"
              />
            </div>
          </div>
          <div className="absolute -bottom-4 left-6 border border-border bg-white px-5 py-3">
            <p className="font-heading text-sm font-bold text-ink">Lancer Smith, M.S.</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">Principal Physicist</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="faq-section" className="border-b border-border bg-blueprint-grid">
    <JsonLd
      id="faq"
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }}
    />
    <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:px-8 md:py-28 lg:grid-cols-[0.8fr_1.2fr]">
      <Reveal>
        <p className="eyebrow">FAQ</p>
        <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink md:text-xl">
          Radiation shielding design, answered
        </h2>
        <p className="mt-5 max-w-sm text-sm leading-relaxed text-ink/60">
          Requirements vary by state and project type. For anything not covered here, email Lancer directly.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={`mailto:${EMAIL}`} data-testid="faq-cta-email" className="group flex items-center gap-2 bg-blueprint px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink">
            <Mail size={15} /> {EMAIL}
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="border border-border bg-white" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border px-6">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading text-sm font-bold text-ink hover:text-blueprint hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${i}`} className="text-sm leading-relaxed text-ink/65">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const InsightsTeaser = () => (
  <section data-testid="insights-teaser-section" className="border-b border-border bg-white">
    <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
      <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="eyebrow">From the journal</p>
          <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink md:text-xl">
            Practical guidance
          </h2>
        </div>
        <Link to="/insights" data-testid="insights-teaser-all" className="group flex w-fit items-center gap-2 text-sm font-semibold text-blueprint transition-colors hover:text-ink">
          All insights <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="mt-10 border-t border-border">
        {ARTICLES.slice(0, 3).map((a, i) => (
          <Reveal key={a.slug} delay={i * 0.07}>
            <Link
              to={`/insights/${a.slug}`}
              data-testid={`insights-teaser-${a.slug}`}
              className="group grid gap-2 border-b border-border py-6 transition-colors duration-200 hover:bg-paper md:grid-cols-[60px_1fr] md:items-center md:gap-8 md:px-4"
            >
              <span className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-heading text-base font-bold tracking-tight text-ink transition-colors duration-200 group-hover:text-blueprint">
                {a.title}
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta-section" className="bg-white">
    <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
      <Reveal>
        <div className="relative overflow-hidden border border-ink bg-ink p-10 text-white md:p-16">
          <div className="spotlight-dark absolute inset-0" />
          <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
            <div>
              <p className="eyebrow !text-azure">Primary next step</p>
              <h2 className="mt-4 max-w-xl font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
                Start your shielding project today.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                Share the basics through the structured request form — most single-room designs are $950, prepaid at intake, with a 90-day money-back guarantee.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/request"
                data-testid="final-cta-request"
                className="group flex items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-azure hover:text-white"
              >
                Request a Shielding Design
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="final-cta-email"
                className="flex items-center gap-2 border border-white/30 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:border-azure hover:text-azure"
              >
                <Mail size={16} /> Email Lancer@HeartlandPhysics.com
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  usePageMeta(
    "Radiation Shielding Design & Plan Review | Heartland Physics",
    "Physicist-prepared shielding designs for X-ray, CT, fluoroscopy, dental, mammography, and veterinary rooms. State-specific reports, fixed $950 single-room pricing."
  );
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <ValueProp />
      <ReportIncludes />
      <Services />
      <Process />
      <PromiseBand />
      <WhyItMatters />
      <Faq />
      <InsightsTeaser />
      <FinalCta />
    </>
  );
};

export default Home;
